<script setup lang="ts">
import type { CreateInsurancePolicyInput, PaymentSchedule, PolicyType } from '../internal/types.ts'
import { parseLocalDate } from '@taler/domain'
import { ActionButton, Select, StatusTag } from '@taler/ui'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const props = defineProps<{ isSubmitting: boolean; errorMessage: string | undefined }>()
const emit = defineEmits<{ create: [CreateInsurancePolicyInput] }>()

const { t } = useI18n()
const open = ref(false)
const insurer = ref('')
const policyNumber = ref('')
const type = ref<PolicyType | undefined>(undefined)
const coverageStart = ref('')
const coverageEnd = ref('')
const paymentSchedule = ref<PaymentSchedule | undefined>(undefined)
const notes = ref('')
const validationError = ref<string | undefined>(undefined)

function reset(): void {
  insurer.value = ''
  policyNumber.value = ''
  type.value = undefined
  coverageStart.value = ''
  coverageEnd.value = ''
  paymentSchedule.value = undefined
  notes.value = ''
  validationError.value = undefined
}

function strOrUndef(value: string): string | undefined {
  const trimmed = value.trim()
  return trimmed === '' ? undefined : trimmed
}

function submit(): void {
  validationError.value = undefined
  if (insurer.value.trim() === '' || type.value === undefined) return
  const input: CreateInsurancePolicyInput = {
    insurer: insurer.value.trim(),
    type: type.value,
    ...(strOrUndef(policyNumber.value) === undefined
      ? {}
      : { policyNumber: strOrUndef(policyNumber.value)! }),
    ...(coverageStart.value === '' ? {} : { coverageStart: parseLocalDate(coverageStart.value) }),
    ...(coverageEnd.value === '' ? {} : { coverageEnd: parseLocalDate(coverageEnd.value) }),
    ...(paymentSchedule.value === undefined ? {} : { paymentSchedule: paymentSchedule.value }),
    ...(strOrUndef(notes.value) === undefined ? {} : { notes: strOrUndef(notes.value)! }),
  }
  emit('create', input)
}

watch(
  () => props.isSubmitting,
  (submitting, wasSubmitting) => {
    if (wasSubmitting && !submitting && props.errorMessage === undefined) {
      reset()
      open.value = false
    }
  },
)

const typeOptions = computed(() => [
  { value: 'THIRD_PARTY' as const, label: t('garage.policyType.THIRD_PARTY') },
  { value: 'CASCO' as const, label: t('garage.policyType.CASCO') },
  { value: 'COMPREHENSIVE' as const, label: t('garage.policyType.COMPREHENSIVE') },
  { value: 'OTHER' as const, label: t('garage.policyType.OTHER') },
])
const scheduleOptions = computed(() => [
  { value: 'ANNUAL' as const, label: t('garage.paymentSchedule.ANNUAL') },
  { value: 'SEMI_ANNUAL' as const, label: t('garage.paymentSchedule.SEMI_ANNUAL') },
  { value: 'QUARTERLY' as const, label: t('garage.paymentSchedule.QUARTERLY') },
  { value: 'MONTHLY' as const, label: t('garage.paymentSchedule.MONTHLY') },
])
const combinedError = computed(() => validationError.value ?? props.errorMessage)
</script>

<template>
  <div>
    <ActionButton v-if="!open" type="button" @click="open = true">
      {{ t('garage.policies.add') }}
    </ActionButton>

    <form
      v-else
      class="flex flex-col gap-3 rounded-[var(--radius-surface)] border border-[color:var(--color-border)] p-4 bg-[color:var(--color-surface)]"
      @submit.prevent="submit"
    >
      <div class="grid grid-cols-2 gap-[var(--space-cell)]">
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('garage.policies.insurer')
          }}</span>
          <input
            v-model="insurer"
            type="text"
            required
            :placeholder="t('garage.policies.insurerPlaceholder')"
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('garage.policies.policyNumber')
          }}</span>
          <input
            v-model="policyNumber"
            type="text"
            :placeholder="t('garage.policies.policyNumberPlaceholder')"
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('garage.policies.type')
          }}</span>
          <Select v-model="type" :options="typeOptions" :placeholder="t('garage.policies.selectType')" />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('garage.policies.paymentSchedule')
          }}</span>
          <Select
            v-model="paymentSchedule"
            :options="scheduleOptions"
            :placeholder="t('garage.policies.selectPaymentSchedule')"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('garage.policies.coverageStart')
          }}</span>
          <input
            v-model="coverageStart"
            type="date"
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
          />
        </label>
        <label class="flex flex-col gap-1">
          <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
            t('garage.policies.coverageEnd')
          }}</span>
          <input
            v-model="coverageEnd"
            type="date"
            class="h-[var(--density-control)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] text-[color:var(--color-text)]"
          />
        </label>
      </div>

      <label class="flex flex-col gap-1">
        <span class="text-[length:var(--text-step-2)] font-medium text-[color:var(--color-text)]">{{
          t('garage.policies.notes')
        }}</span>
        <textarea
          v-model="notes"
          rows="2"
          :placeholder="t('garage.policies.notesPlaceholder')"
          class="rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] bg-[color:var(--color-surface)] px-[var(--space-cell)] py-2 text-[color:var(--color-text)]"
        />
      </label>

      <StatusTag v-if="combinedError" tone="danger">{{ combinedError }}</StatusTag>

      <div class="flex gap-2">
        <ActionButton type="submit" :disabled="isSubmitting">
          {{ t('garage.policies.create') }}
        </ActionButton>
        <button
          type="button"
          class="min-h-[var(--density-target-min)] rounded-[var(--radius-control)] border border-[color:var(--color-border-control)] px-3 py-2 text-[color:var(--color-text)]"
          @click="open = false"
        >
          {{ t('garage.policies.cancel') }}
        </button>
      </div>
    </form>
  </div>
</template>
